/**
 * The team screen's writes, one function per intent. Each sends the reason
 * the person typed, the role's `version` where the endpoint takes one, and the
 * intent's Idempotency-Key (ADR-0016 §2.1). Nothing is applied to the screen
 * until the server answers: the caller updates from the response.
 */
import type { DepartmentId } from "@wallyo/contracts/workspaces";
import type {
  EditRoleRequest,
  GetStaffMeResponse,
  MoneyLimit,
  StaffMemberListItem,
  StaffRoleListItem,
} from "@wallyo/contracts/console";
import type { StaffFeatureId } from "@wallyo/contracts/actions";
import type { DefinedPermissionId } from "@wallyo/contracts/permissions";
import type { ConsoleClient } from "../../api/client";
import {
  assignStaffRole,
  createRole,
  deactivateStaff,
  reactivateStaff,
  editRole,
  endStaffSessions,
  inviteStaff,
  removeStaffRole,
  setRoleFeature,
  setRolePermission,
} from "../../api/console-api";
import { t } from "../../i18n";

/** The server's minimum (`reason` in server/http/validation/console.ts). */
export const REASON_MIN_LENGTH = 3;

/** A problem with the reason, or null when it can be sent. */
export function reasonProblem(reason: string): string | null {
  return reason.trim().length >= REASON_MIN_LENGTH ? null : t("team.reasonTooShort");
}

/** Grants (`enabled`) or revokes a feature on a role, at the version on screen. */
export function sendFeatureChange(
  client: ConsoleClient,
  change: { role: StaffRoleListItem; settingId: StaffFeatureId; enabled: boolean; reason: string },
  idempotencyKey: string,
) {
  return setRoleFeature(
    client,
    change.role.staffRoleId,
    change.settingId,
    {
      enabled: change.enabled,
      reason: change.reason.trim(),
      version: change.role.version,
    },
    { idempotencyKey },
  );
}

// ── A permission on a role (ADR-0019) ────────────────────────────────────

/** One limit as the dialog holds it: both fields as typed. */
export type LimitRow = { currency: string; amount: string };

/** ISO 4217: three letters, the way the server takes them. */
const CURRENCY = /^[A-Za-z]{3}$/;

export const limitRowsOf = (limits: readonly MoneyLimit[]): LimitRow[] =>
  limits.map((limit) => ({
    currency: limit.currency,
    amount: String(limit.amount),
  }));

/** What was typed, as the contract's limits: currencies upper-cased, amounts as numbers. */
export function limitsOfRows(rows: readonly LimitRow[]): MoneyLimit[] {
  return rows.map((row) => ({
    currency: row.currency.trim().toUpperCase(),
    amount: Number(row.amount.trim()),
  }));
}

/** The first problem with the limits typed, or null when they can be sent. */
export function limitsProblem(rows: readonly LimitRow[]): string | null {
  for (const row of rows) {
    if (!CURRENCY.test(row.currency.trim())) return t("team.permission.limitProblem");
    const amount = Number(row.amount.trim());
    if (!row.amount.trim() || !Number.isFinite(amount) || amount <= 0) return t("team.permission.limitProblem");
  }
  const currencies = limitsOfRows(rows).map((limit) => limit.currency);
  const twice = currencies.find((currency, index) => currencies.indexOf(currency) !== index);
  if (twice) return t("team.permission.duplicateCurrency", { currency: twice });
  return null;
}

/**
 * The currency a new row starts in: the one this role's limits use most, and
 * nothing when it has none. No currency is built into the console.
 */
export function defaultCurrency(role: StaffRoleListItem): string {
  const counts = new Map<string, number>();
  for (const grant of role.permissions) {
    for (const limit of grant.limits) counts.set(limit.currency, (counts.get(limit.currency) ?? 0) + 1);
  }
  let best = "";
  let most = 0;
  for (const [currency, count] of counts) {
    if (count > most) {
      best = currency;
      most = count;
    }
  }
  return best;
}

/** The currencies to suggest: the ones this role and this person already work in. */
export function currencySuggestions(role: StaffRoleListItem, me: GetStaffMeResponse): string[] {
  const seen = new Set<string>();
  for (const grant of role.permissions) for (const limit of grant.limits) seen.add(limit.currency);
  for (const held of me.permissions) for (const limit of held.limits) seen.add(limit.currency);
  return [...seen].sort();
}

/** Grants, revokes or re-limits a permission on a role, at the version on screen. */
export function sendPermissionChange(
  client: ConsoleClient,
  change: {
    role: StaffRoleListItem;
    settingId: DefinedPermissionId;
    enabled: boolean;
    /** The whole set of limits, or undefined to leave them as they are. */
    limits?: readonly MoneyLimit[];
    reason: string;
  },
  idempotencyKey: string,
) {
  return setRolePermission(
    client,
    change.role.staffRoleId,
    change.settingId,
    {
      enabled: change.enabled,
      ...(change.limits === undefined ? {} : { limits: change.limits }),
      reason: change.reason.trim(),
      version: change.role.version,
    },
    { idempotencyKey },
  );
}

export type RoleFields = {
  name: string;
  description: string;
  /** Which workspace its holders land in. "" means none (WALLIYO-0132). */
  department: DepartmentId | "";
};

/** A problem with a role's name or description, or null. */
export function roleFieldsProblem(fields: RoleFields, role?: StaffRoleListItem): string | null {
  if (fields.name.trim().length < 2) return t("team.role.nameTooShort");
  // A new role has to say where it belongs. An existing one can be taken out
  // of every workspace deliberately, which is what clearing it means.
  if (!role && fields.department === "") return t("roles.new.departmentNeeded");
  if (role && !editRoleChanges(role, fields)) return t("team.role.unchanged");
  return null;
}

/** Only what changed, so an edit never rewrites what it didn't touch. Null when nothing did. */
export function editRoleChanges(
  role: StaffRoleListItem,
  fields: RoleFields,
): Pick<EditRoleRequest, "name" | "description" | "department"> | null {
  const changes: Pick<EditRoleRequest, "name" | "description" | "department"> = {};
  const name = fields.name.trim();
  const description = fields.description.trim() || null;
  const department = fields.department === "" ? null : fields.department;
  if (name !== role.name) changes.name = name;
  if (description !== (role.description ?? null)) changes.description = description;
  if (department !== role.department) changes.department = department;
  return Object.keys(changes).length > 0 ? changes : null;
}

export function sendCreateRole(client: ConsoleClient, fields: RoleFields, reason: string, idempotencyKey: string) {
  return createRole(
    client,
    {
      name: fields.name.trim(),
      description: fields.description.trim() || null,
      // Required on create; the form refuses an empty one before this.
      department: fields.department as DepartmentId,
      reason: reason.trim(),
    },
    { idempotencyKey },
  );
}

export function sendEditRole(
  client: ConsoleClient,
  role: StaffRoleListItem,
  fields: RoleFields,
  reason: string,
  idempotencyKey: string,
) {
  return editRole(
    client,
    role.staffRoleId,
    { ...editRoleChanges(role, fields), version: role.version, reason: reason.trim() },
    { idempotencyKey },
  );
}

export type InviteFields = { name: string; email: string; phone: string; roleIds: string[] };

export function inviteProblem(fields: InviteFields): string | null {
  return fields.name.trim() && fields.email.trim() && fields.phone.trim() ? null : t("team.invite.missing");
}

export function sendInvite(client: ConsoleClient, fields: InviteFields, reason: string, idempotencyKey: string) {
  return inviteStaff(
    client,
    {
      name: fields.name.trim(),
      email: fields.email.trim(),
      phone: fields.phone.replace(/[\s-]/g, ""),
      reason: reason.trim(),
      // Their roles, in the same request (WALLIYO-0120). Someone invited with
      // none signs in to a console with nothing open to them, so the dialog
      // offers the list rather than leaving it to a second step.
      roleIds: fields.roleIds,
    },
    { idempotencyKey },
  );
}

export function sendDeactivate(
  client: ConsoleClient,
  member: StaffMemberListItem,
  reason: string,
  idempotencyKey: string,
) {
  return deactivateStaff(client, member.staffMemberId, { reason: reason.trim() }, { idempotencyKey });
}

export function sendReactivate(
  client: ConsoleClient,
  member: StaffMemberListItem,
  reason: string,
  idempotencyKey: string,
) {
  return reactivateStaff(client, member.staffMemberId, { reason: reason.trim() }, { idempotencyKey });
}

export function sendEndSessions(
  client: ConsoleClient,
  member: StaffMemberListItem,
  reason: string,
  idempotencyKey: string,
) {
  return endStaffSessions(client, member.staffMemberId, { reason: reason.trim() }, { idempotencyKey });
}

export function sendMembership(
  client: ConsoleClient,
  change: { member: StaffMemberListItem; role: { staffRoleId: string }; assign: boolean; reason: string },
  idempotencyKey: string,
) {
  const request = { reason: change.reason.trim() };
  return change.assign
    ? assignStaffRole(client, change.member.staffMemberId, change.role.staffRoleId, request, { idempotencyKey })
    : removeStaffRole(client, change.member.staffMemberId, change.role.staffRoleId, request, { idempotencyKey });
}

/** Replaces the item with the same id, or adds it; keeps the list's order. */
export function upsert<T>(list: readonly T[], item: T, idOf: (item: T) => string): T[] {
  const index = list.findIndex((each) => idOf(each) === idOf(item));
  if (index === -1) return [...list, item];
  return list.map((each, at) => (at === index ? item : each));
}
